import React, { useEffect, useState } from 'react';
import { formatDate } from '../../../utils/dateUtils';
import { MemberStatus } from './MemberStatus';
import { AICreditsEditor } from './credits/AICreditsEditor';
import { PaymentHistoryModal } from './PaymentHistoryModal';
import { MemberDeleteModal } from './MemberDeleteModal';
import { CancelDeletionModal } from './CancelDeletionModal';
import { RoleSelector } from './RoleSelector';
import { Trash2, RotateCcw, User, CreditCard, Calendar, Sparkles, Hash, Coins, Shield } from 'lucide-react';
import type { MemberExtends } from '../../../types/member';
import { SUBSCRIPTION_PLANS } from '../../../utils/stripe/config';

interface MemberListItemProps {
  member: MemberExtends;
  onUpdateMember: (memberId: string, updates: Partial<MemberExtends>) => void;
  onDeleteMember?: (memberId: string) => void;
  onCancelDeletion?: (memberId: string) => void;
  isSelected?: boolean;
  onSelect?: (selected: boolean) => void;
  startDate?: string;
  endDate?: string;
}

export function MemberListItem({ 
  member, 
  onUpdateMember,
  onDeleteMember,
  onCancelDeletion,
  isSelected = false,
  onSelect,
  startDate,
  endDate
}: MemberListItemProps) {
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRoleSelector, setShowRoleSelector] = useState(false);

  const handleUpdateCredits = (memberId: string, newCredits: number) => {
    onUpdateMember(memberId, { aiCredits: newCredits });
  };

  const handleDelete = () => {
    if (onDeleteMember) {
      onDeleteMember(member.id);
    }
    setShowDeleteModal(false);
  };

  const handleCancelDeletion = () => {
    if (onCancelDeletion) {
      onCancelDeletion(member.id);
    }
    setShowCancelModal(false);
  };


  const isDeletionScheduled = member.status === 'suspended' && member.scheduledDeletionTime;
  const remainingTime = isDeletionScheduled && member.scheduledDeletionTime
    ? new Date(member.scheduledDeletionTime).getTime() - Date.now()
    : 0;
  const remainingHours = Math.max(0, Math.floor(remainingTime / (1000 * 60 * 60)));
  const remainingMinutes = Math.max(0, Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60)));

  return (
    <>
      {/* Desktop View */}
      <div className="hidden lg:grid lg:grid-cols-9 gap-4 px-6 py-4 hover:bg-gray-50">
        <div className="flex items-center gap-3">
          {onSelect && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          )}
          <div className="text-sm text-gray-900">{member.memberNumber}</div>
        </div>
        <div className="text-sm text-gray-500 truncate" title={member.email}>{member.email}</div>
        <div className="text-sm text-gray-900 truncate">              
          {SUBSCRIPTION_PLANS.find(s => s.id === member.plan)?.name ?? '未加入'}
        </div>
        <div className="text-sm text-gray-500">{formatDate(member.registeredAt)}</div>
        <div>
          <AICreditsEditor 
            member={member}
            onUpdate={(newCredits) => handleUpdateCredits(member.id, newCredits)}
          />
        </div>
        <div className="text-sm text-gray-500 truncate" title={member.referralCode || '-'}>
          {member.referralCode || '-'}
        </div>
        <div>
          <button
            onClick={() => setShowPaymentHistory(true)}
            className="text-sm font-medium text-gray-900 hover:text-blue-600"
          >
            ¥{member.salesAmount.toLocaleString()}
          </button>
        </div>
        <div className={`flex items-start gap-2 ${isDeletionScheduled ? 'flex-col' : ' justify-arround'}`}>
          <MemberStatus status={member.status} />
          {isDeletionScheduled ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-600">
                残り: {remainingHours}h {remainingMinutes}m
              </span>
              <button
                onClick={() => setShowCancelModal(true)}
                className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
                title="削除をキャンセル"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
              title="強制退会"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            member.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {member.role === 'admin' ? '管理者' : 'ユーザー'}
          </span>
          <button
            onClick={() => setShowRoleSelector(!showRoleSelector)}
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
            title="ロールを変更"
          >
            <Shield className="w-4 h-4" />
          </button>

            {showRoleSelector && (
              <div className="absolute z-10 mt-2 right-0 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                <RoleSelector 
                  userId={member.id} 
                  currentRole={member.role || 'user'} 
                  onClose={()=> setShowRoleSelector(false)}
                />
              </div>
            )}
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden p-4 hover:bg-gray-50 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onSelect && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => onSelect(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            )}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">{member.memberNumber}</span>
              </div>
              <div className="text-sm text-gray-500 truncate" title={member.email}>{member.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MemberStatus status={member.status} />
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              member.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {member.role === 'admin' ? '管理者' : 'ユーザー'}
            </span>
            <button
              onClick={() => setShowRoleSelector(!showRoleSelector)}
              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
              title="ロールを変更"
            >
              <Shield className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <CreditCard className="w-4 h-4" />
              <span>プラン</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {SUBSCRIPTION_PLANS.find(s => s.id === member.plan)?.name ?? '未加入'}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Calendar className="w-4 h-4" />
              <span>登録日</span>
            </div>
            <span className="text-sm text-gray-900">{formatDate(member.registeredAt)}</span>
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Sparkles className="w-4 h-4" />
              <span>AIクレジット</span>
            </div>
            <AICreditsEditor 
              member={member}
              onUpdate={(newCredits) => handleUpdateCredits(member.id, newCredits)}
            />
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Hash className="w-4 h-4" />
              <span>紹介者コード</span>
            </div>
            <span className="text-sm text-gray-900 truncate" title={member.referralCode || '-'}>{member.referralCode || '-'}</span>
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
              <Coins className="w-4 h-4" />
              <span>売上金</span>
            </div>
            <button
              onClick={() => setShowPaymentHistory(true)}
              className="text-sm font-medium text-gray-900 hover:text-blue-600"
            >
              ¥{member.salesAmount.toLocaleString()}
            </button>
          </div>
        </div>

        {showRoleSelector && (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <RoleSelector 
              userId={member.id} 
              currentRole={member.role || 'user'} 
              onClose={()=> setShowRoleSelector(false)}
            />
          </div>
        )}

        {isDeletionScheduled ? (
          <div className="flex items-center justify-between pt-2 border-t">
            <span className="text-xs text-red-600 truncate">
              残り: {remainingHours}h {remainingMinutes}m で削除
            </span>
            <button
              onClick={() => setShowCancelModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span>取り消し</span>
            </button>
          </div>
        ) : (
          <div className="flex justify-end pt-2 border-t">
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span>強制退会</span>
            </button>
          </div>
        )}
      </div>

      {showPaymentHistory && (
        <PaymentHistoryModal
          member={member}
          onClose={() => setShowPaymentHistory(false)}
          startDate={startDate}
          endDate={endDate}
        />
      )}

      {showDeleteModal && (
        <MemberDeleteModal
          member={member}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {showCancelModal && (
        <CancelDeletionModal
          member={member}
          onConfirm={handleCancelDeletion}
          onCancel={() => setShowCancelModal(false)}
        />
      )}
    </>
  );
}